import { createValidationSchema, PosixDate, stringField, uuidField, } from "@for-it/domain-lib";
import { TenantEntity } from "../../entities/tenant.js";
import { TenantNotFoundError } from "../../errors/tenant-not-found-error.js";
const FALLBACK_PRIMARY = "#3B82F6";
const FALLBACK_SECONDARY = "#10B981";
export const updateBranding = {
    name: "updateBranding",
    description: "Updates the singleton branding row of a tenant's DB. Only fields present in the payload are updated.",
    type: "command",
    isAuthRequired: true,
    payloadSchema: createValidationSchema({
        tenantId: uuidField({
            required: true,
            message: "Debe ingresar un ID de tenant válido",
        }),
        appName: stringField({
            required: false,
            message: "El nombre de la app es opcional",
        }),
        logoUrl: stringField({
            required: false,
            message: "El logoUrl es opcional",
        }),
        faviconUrl: stringField({
            required: false,
            message: "El faviconUrl es opcional",
        }),
        primaryColor: stringField({
            required: false,
            message: "El color primario es opcional",
        }),
        secondaryColor: stringField({
            required: false,
            message: "El color secundario es opcional",
        }),
    }),
    useCase: async ({ db, tenantConnectionResolver }, payload) => {
        const tenant = await db.findOne(TenantEntity, {
            where: { id: payload.tenantId },
        });
        if (!tenant)
            return new TenantNotFoundError();
        const connection = await tenantConnectionResolver.open(tenant.dbName);
        try {
            const existing = await connection.manager.query(`SELECT appName, logoUrl, faviconUrl, primaryColor, secondaryColor
                 FROM brandings
                 WHERE id = ?
                 LIMIT 1`, ["default"]);
            const first = Array.isArray(existing) && existing.length > 0
                ? existing[0]
                : null;
            const now = new PosixDate();
            if (!first) {
                // No row yet (edge case for tenants seeded before phase 3
                // wired the default Branding row). Insert one.
                await connection.manager.query(`INSERT INTO brandings
                       (id, createdAt, updatedAt, deletedAt,
                        appName, logoUrl, faviconUrl,
                        primaryColor, secondaryColor, bankAlias)
                     VALUES (?, ?, ?, NULL, ?, ?, ?, ?, ?, NULL)`, [
                    "default",
                    now.timestamp,
                    now.timestamp,
                    payload.appName ?? tenant.organizationName,
                    payload.logoUrl ?? null,
                    payload.faviconUrl ?? null,
                    payload.primaryColor ?? FALLBACK_PRIMARY,
                    payload.secondaryColor ?? FALLBACK_SECONDARY,
                ]);
            }
            else {
                const next = {
                    appName: payload.appName !== undefined
                        ? payload.appName
                        : (first.appName ?? tenant.organizationName),
                    logoUrl: payload.logoUrl !== undefined
                        ? payload.logoUrl
                        : (first.logoUrl ?? null),
                    faviconUrl: payload.faviconUrl !== undefined
                        ? payload.faviconUrl
                        : (first.faviconUrl ?? null),
                    primaryColor: payload.primaryColor !== undefined
                        ? payload.primaryColor
                        : (first.primaryColor ?? FALLBACK_PRIMARY),
                    secondaryColor: payload.secondaryColor !== undefined
                        ? payload.secondaryColor
                        : (first.secondaryColor ?? FALLBACK_SECONDARY),
                };
                await connection.manager.query(`UPDATE brandings
                       SET appName = ?,
                           logoUrl = ?,
                           faviconUrl = ?,
                           primaryColor = ?,
                           secondaryColor = ?,
                           updatedAt = ?
                     WHERE id = ?`, [
                    next.appName,
                    next.logoUrl,
                    next.faviconUrl,
                    next.primaryColor,
                    next.secondaryColor,
                    now.timestamp,
                    "default",
                ]);
            }
            const updated = await connection.manager.query(`SELECT appName, logoUrl, faviconUrl, primaryColor, secondaryColor
                 FROM brandings
                 WHERE id = ?
                 LIMIT 1`, ["default"]);
            const row = Array.isArray(updated) && updated.length > 0
                ? updated[0]
                : null;
            return {
                appName: String(row?.appName ?? payload.appName ?? tenant.organizationName),
                logoUrl: row?.logoUrl ?? null,
                faviconUrl: row?.faviconUrl ?? null,
                primaryColor: String(row?.primaryColor ??
                    payload.primaryColor ??
                    FALLBACK_PRIMARY),
                secondaryColor: String(row?.secondaryColor ??
                    payload.secondaryColor ??
                    FALLBACK_SECONDARY),
            };
        }
        finally {
            await connection.release();
        }
    },
};
